
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import patientServices from '../../../core/services/patientServices';
import { bookingPlanService } from '../../../core/services/bookingplanService';

// ── helpers ───────────────────────────────────────────────
const getName = (p) => {
  // Build full name from fname + mname + lname when available
  const parts = [p?.fname, p?.mname, p?.lname].filter(Boolean);
  if (parts.length > 0) return parts.join(' ').trim();
  // Fall back to other name fields
  return p?.patient_name || p?.first_name || p?.name || '';
};

const getPhone = (p) =>
  p?.phone || p?.phno || p?.phoneno || '';

const getAadhaar = (p) =>
  p?.adhar || p?.aadhar || p?.adhaar || '';

const getCode = (p) =>
  p?.patientcode || p?.patient_code || p?.patientCode || '';

const getId = (p) =>
  String(p?.id || p?.patient_id || p?.patientId || '');

const getMain = (p) =>
  p?.mainnumber || p?.main || p?.MAIN || '';

const getLastVisit = (p) => {
  // Try to extract last visit date/time from various possible locations
  const visit = p?.visit || p?.visits?.[0] || {};
  const visitDate = p?.visit_datetime
 || p?.visitDate || p?.lastVisitDate || '';
  if (!visitDate) return '';
  
  try {
    const d = new Date(visitDate);
    if (isNaN(d.getTime())) return visitDate; // fallback to raw string
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    let hours = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) hours = 12;
    return `${day}/${month}/${year} ${hours}:${mins} ${ampm}`;
  } catch {
    return visitDate;
  }
};

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
};

const AVATAR_COLORS = [
  '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b',
  '#ef4444', '#06b6d4', '#6366f1', '#ec4899',
];

const avatarColor = (id) =>
  AVATAR_COLORS[parseInt(id || '0', 10) % AVATAR_COLORS.length];

// Cache size cap so this doesn't grow unbounded across a long session
const MAX_CACHE_ENTRIES = 50;

// ── extractApiList ─────────────────────────────────────────
function extractApiList(res) {
  if (Array.isArray(res))                        return res;
  if (Array.isArray(res?.patient))               return res.patient;
  if (Array.isArray(res?.patientListCollection)) return res.patientListCollection;
  if (Array.isArray(res?.patsearchreslt))        return res.patsearchreslt;
  if (Array.isArray(res?.patientList))           return res.patientList;
  if (Array.isArray(res?.results))               return res.results;
  if (Array.isArray(res?.data))                  return res.data;
  return [];
}

function isPatientLikeObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Boolean(
    value.patientId ||
    value.patient_id ||
    value.patientcode ||
    value.patient_code ||
    value.patientName ||
    value.patient_name ||
    value.fname ||
    value.first_name ||
    value.name ||
    value.visit ||
    value.visit_status ||
    value.visitStatus
  );
}

function collectClosedVisitCandidates(res) {
  const candidates = [];
  const stack = [res];
  const seen = new Set();

  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;

    if (Array.isArray(node)) {
      node.forEach((item) => stack.push(item));
      continue;
    }

    if (typeof node !== 'object') continue;
    if (seen.has(node)) continue;
    seen.add(node);

    if (isPatientLikeObject(node)) {
      candidates.push(node);
    }

    Object.values(node).forEach((value) => {
      if (value && typeof value === 'object') {
        stack.push(value);
      }
    });
  }

  return candidates;
}

const getVisitStatus = (patient) => {
  if (!patient) return '';
  return String(
    patient.visit_status ||
    patient.visitStatus ||
    patient.visit?.status ||
    patient.visit?.visit_status ||
    ''
  ).trim().toLowerCase();
};

const hasClosedVisit = (patient) => {
  const directStatus = getVisitStatus(patient);
  return directStatus === 'close' || directStatus === 'closed';
};

const hasOpenVisit = (patient) => getVisitStatus(patient) === 'open';

// ── HighlightText — bolds matching chars in a result ──────
function HighlightText({ text, query }) {
  if (!query || !text) return <span>{text || ''}</span>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <mark style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: 3, padding: '0 1px' }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </span>
  );
}

// ── PatientCard — single row in the dropdown ──────────────
function PatientCard({ patient, query, onSelect }) {
  const name    = getName(patient);
  const phone   = getPhone(patient);
  const aadhar  = getAadhaar(patient);
  const code    = getCode(patient);
  const id      = getId(patient);
  const main    = getMain(patient);
  const lastVisit = getLastVisit(patient);
  const color   = avatarColor(id);
  const initials = getInitials(name);
  const visitStatus = getVisitStatus(patient);
  const isOpen = visitStatus === 'open';
  const isClosed = visitStatus === 'close' || visitStatus === 'closed';

  return (
    <button
      type="button"
      className="psi-item"
      onMouseDown={(e) => { e.preventDefault(); onSelect(patient); }}
    >
      <div className="psi-avatar" style={{ background: color }}>
        {initials}
      </div>
      <div className="psi-info">
        <div className="psi-name">
          <HighlightText text={name || 'Unknown'} query={query} />
          {isOpen && (
            <span style={{
              marginLeft: 8, fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              color: '#15803d', background: '#dcfce7', borderRadius: 4, padding: '2px 6px',
            }}>
              Open Visit
            </span>
          )}
          {isClosed && (
            <span style={{
              marginLeft: 8, fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              color: '#475569', background: '#e2e8f0', borderRadius: 4, padding: '2px 6px',
            }}>
              Closed Visit
            </span>
          )}
        </div>
        <div className="psi-meta">
          {phone && (
            <span className="psi-tag psi-tag--phone">
              📞 <HighlightText text={phone} query={query} />
            </span>
          )}
          
          {/* {code && (
            <span className="psi-tag psi-tag--code">
              # <HighlightText text={code} query={query} />
            </span>
          )} */}
          {id && (
            <span className="psi-tag psi-tag--id">
              MAIN: <HighlightText text={id} query={query} />
            </span>
          )}
          {main && (
            <span className="psi-tag psi-tag--main">
              MAIN: <HighlightText text={String(main)} query={query} />
            </span>
          )}
        </div>
        {lastVisit && (
          <div className="psi-last-visit">
            🕐 Last Visit: {lastVisit}
          </div>
        )}
      </div>
      <div className="psi-arrow">›</div>
    </button>
  );
}

// ── Main export ───────────────────────────────────────────
/**
 * PatientSearchInput
 *
 * Props:
 *   preloadedPatients  {Array}    - already-fetched patient list for instant local search
 *   onSelect           {fn}       - called with patient object on selection
 *   value              {string}   - controlled display value (patient name)
 *   placeholder        {string}
 *   error              {string}
 *   disabled           {boolean}
 *   label              {string|node}
 *   onClear            {fn}       - called when ✕ is clicked
 */
export default function PatientSearchInput({
  preloadedPatients = [],
  onSelect,
  value = '',
  placeholder = 'Search by Name / Phone / Aadhaar / UHID / Code…',
  error,
  disabled,
  label,
  onClear,
  restrictToClosedVisits = false,
  onAddNewPatient,
}) {
  const [query,        setQuery]        = useState('');
  const [open,         setOpen]         = useState(false);
  const [results,      setResults]      = useState([]);
  const [searching,    setSearching]    = useState(false);
  const [hasSelected,  setHasSelected]  = useState(Boolean(value));

  const apiTimer      = useRef(null);
  const localTimer    = useRef(null);
  const containerRef  = useRef(null);
  const inputRef      = useRef(null);

  // Monotonically increasing sequence number — guards against a slow,
  // stale API response overwriting the results of a newer, faster one.
  const searchSeq     = useRef(0);

  // Simple in-memory cache of recent query → API result, keyed by the
  // lowercased query string. Avoids re-hitting the backend when the user
  // retypes/backspaces to a query already seen this session.
  const searchCache   = useRef(new Map());

  // ── Precomputed, lowercased search index ────────────────
  // Built once per preloadedPatients change instead of re-deriving every
  // field (name/phone/aadhaar/code/id/main) + lowercasing it on every
  // keystroke. This is the biggest win for large patient lists.
  const searchIndex = useMemo(() => {
    return preloadedPatients.map((p) => ({
      patient: p,
      name:   getName(p).toLowerCase(),
      phone:  getPhone(p).toLowerCase(),
      aadhar: getAadhaar(p).toLowerCase(),
      code:   getCode(p).toLowerCase(),
      id:     getId(p).toLowerCase(),
      main:   String(getMain(p)).toLowerCase(),
    }));
  }, [preloadedPatients]);

  // Sync external value → display
  useEffect(() => {
    setHasSelected(Boolean(value));
    if (value && !open) setQuery(value);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click-outside to close
  useEffect(() => {
    function handleOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const cacheSet = (key, value_) => {
    const cache = searchCache.current;
    // Evict oldest entry if we're at capacity (Map preserves insertion order)
    if (!cache.has(key) && cache.size >= MAX_CACHE_ENTRIES) {
      const oldestKey = cache.keys().next().value;
      cache.delete(oldestKey);
    }
    cache.set(key, value_);
  };

  // ── Local + API search ────────────────────────────────
  const runSearch = useCallback((q) => {
    if (q.length < 1) {
      setResults([]);
      setSearching(false);
      return;
    }

    const ql = q.toLowerCase();

    if (restrictToClosedVisits) {
      setSearching(true);
      if (apiTimer.current) clearTimeout(apiTimer.current);
      const mySeq = ++searchSeq.current;
      apiTimer.current = setTimeout(async () => {
        try {
          // Search both closed- and open-visit patients so a patient with an
          // existing open visit can still be found and booked (the caller
          // then reuses that open visit instead of creating a new one).
          const [closedSettled, openSettled] = await Promise.allSettled([
            bookingPlanService.getClosedVisitPatients(q),
            bookingPlanService.getOpenVisitPatients(q),
          ]);
          if (mySeq !== searchSeq.current) return; // superseded by a newer search

          const closedList = closedSettled.status === 'fulfilled'
            ? extractApiList(closedSettled.value).filter(hasClosedVisit)
            : [];
          if (closedSettled.status === 'rejected') {
            console.error('[PatientSearchInput] Closed-visit fetch failed:', closedSettled.reason);
          }

          const openList = openSettled.status === 'fulfilled'
            ? extractApiList(openSettled.value).filter(hasOpenVisit)
            : [];
          if (openSettled.status === 'rejected') {
            console.error('[PatientSearchInput] Open-visit fetch failed:', openSettled.reason);
          }

          // Open visits first — they're the more actionable/urgent match.
          const seen = new Set();
          const merged = [];
          [...openList, ...closedList].forEach((p) => {
            const pid = getId(p);
            if (pid && seen.has(pid)) return;
            if (pid) seen.add(pid);
            merged.push(p);
          });

          setResults(merged);
          setOpen(true);
        } catch (err) {
          console.error('[PatientSearchInput] Open/closed visit fetch failed:', err);
          if (mySeq === searchSeq.current) setResults([]);
        } finally {
          if (mySeq === searchSeq.current) setSearching(false);
        }
      }, 200);
      return;
    }

    // ── 1. Instant local filter (no delay) ──────────────
    // Filters the precomputed, already-lowercased index — no per-keystroke
    // field extraction or lowercasing work.
    const local = searchIndex
      .filter(({ name, phone, aadhar, code, id, main }) =>
        name.includes(ql)   ||
        phone.includes(ql)  ||
        aadhar.includes(ql) ||
        code.includes(ql)   ||
        id === ql           ||
        main.includes(ql)
      )
      .map((entry) => entry.patient);

    setResults(local);
    setSearching(false);
    setOpen(true);

    // ── 2. API fallback (only for 3+ chars, debounced 150ms) ──
    // Bumped from 2 to 3 chars: 1-2 char queries return large, low-value
    // result sets and are the most expensive calls to the backend.
    if (q.length < 3) return;

    // Serve from cache if we've already searched this exact string this session
    if (searchCache.current.has(ql)) {
      const cachedList = searchCache.current.get(ql);
      const seen = new Set(local.map(getId));
      const merged = [...local];
      cachedList.forEach((p) => {
        const pid = getId(p);
        if (!seen.has(pid)) { seen.add(pid); merged.push(p); }
      });
      setResults(merged);
      return; // no network call needed
    }

    if (apiTimer.current) clearTimeout(apiTimer.current);
    const mySeq = ++searchSeq.current;
    apiTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await patientServices.patientSearch({
          bodyParams: { searchString: q, flag: "" },
          dateFrom: "",
          dateTo: "",
          page: 0,
          pageSize: 16,
        });

        // A newer keystroke already started a fresher search — discard this one
        if (mySeq !== searchSeq.current) return;

        const apiList = extractApiList(res);
        cacheSet(ql, apiList);

        if (apiList.length > 0) {
          const seen     = new Set(local.map(getId));
          const merged   = [...local];
          apiList.forEach((p) => {
            const pid = getId(p);
            if (!seen.has(pid)) { seen.add(pid); merged.push(p); }
          });
          setResults(merged);
        }
      } catch (err) {
        console.error('[PatientSearchInput] API fallback failed:', err);
      } finally {
        if (mySeq === searchSeq.current) setSearching(false);
      }
    }, 150);
  }, [searchIndex, restrictToClosedVisits]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setHasSelected(false);
    // Run local filter instantly (no debounce)
    runSearch(val.trim());
    if (val.trim().length >= 1) setOpen(true);
    else setResults([]);
  };

  const handleFocus = () => {
    if (!disabled) {
      // If no selection yet, open and re-run last query
      if (!hasSelected) {
        setOpen(true);
        if (query.trim().length >= 1) runSearch(query.trim());
      }
    }
  };

  const handleSelect = (patient) => {
    const displayName = getName(patient) || `Patient #${getId(patient)}`;
    setQuery(displayName);
    setHasSelected(true);
    setOpen(false);
    setResults([]);
    if (onSelect) onSelect(patient);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setQuery('');
    setHasSelected(false);
    setResults([]);
    setOpen(false);
    if (onClear) onClear();
    if (inputRef.current) inputRef.current.focus();
  };

  const displayValue = open ? query : (hasSelected ? value || query : query);

  return (
    <div className={`psi-wrap${disabled ? ' psi-wrap--disabled' : ''}`} ref={containerRef}>
      {label && <label className="psi-label">{label}</label>}

      <div className={`psi-input-wrap${error ? ' psi-input-wrap--error' : ''}`}>
        {/* Search icon */}
        <span className="psi-icon-left" aria-hidden>
          {searching
            ? <span className="psi-spinner" />
            : <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                <circle cx="9" cy="9" r="6" stroke="#94a3b8" strokeWidth="2"/>
                <path d="M13.5 13.5L17 17" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/>
              </svg>
          }
        </span>

        <input
          ref={inputRef}
          type="text"
          className="psi-input"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled}
          aria-autocomplete="list"
          aria-expanded={open}
        />

        {/* Clear button */}
        {(query || hasSelected) && !disabled && (
          <button type="button" className="psi-clear" onClick={handleClear} aria-label="Clear">
            ×
          </button>
        )}

        {/* Chevron */}
        <span className="psi-icon-right" aria-hidden>
          <svg
            width="12" height="12" viewBox="0 0 12 12" fill="none"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }}
          >
            <path d="M2 4l4 4 4-4" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>

      {error && <div className="psi-error">{error}</div>}

      {/* Dropdown */}
      {open && !disabled && (
        <div className="psi-dropdown" role="listbox">
          {/* Add New Patient — only rendered if parent passes the callback */}
          {onAddNewPatient && (
            <div
              onClick={() => {
                setOpen(false);
                onAddNewPatient();
              }}
              style={{
                padding: '10px 14px',
                cursor: 'pointer',
                fontWeight: 600,
                color: '#2563eb',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Add New Patient
            </div>
          )}
          
          {/* Status bar */}
          {query.trim().length >= 1 && (
            <div className="psi-status">
              {searching
                ? <><span className="psi-pulse" />Searching database…</>
                : results.length > 0
                  ? <>Showing {results.length} match{results.length !== 1 ? 'es' : ''}</>
                  : null
              }
            </div>
          )}

          {query.trim().length < 1 && (
            <div className="psi-hint">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="#93c5fd">
                <circle cx="10" cy="10" r="9" stroke="#93c5fd" strokeWidth="1.5" fill="none"/>
                <text x="10" y="15" textAnchor="middle" fontSize="13" fill="#93c5fd">i</text>
              </svg>
              Type to search patients
            </div>
          )}

          {query.trim().length >= 1 && results.length === 0 && !searching && (
            <div className="psi-empty">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <circle cx="16" cy="16" r="10" stroke="#cbd5e1" strokeWidth="2"/>
                <path d="M23 23L30 30" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>No patients found for "<strong>{query.trim()}</strong>"</span>
            </div>
          )}

          {results.length > 0 && (
            <div className="psi-list">
              {results.map((patient, idx) => (
                <PatientCard
                  key={getId(patient) || idx}
                  patient={patient}
                  query={query.trim()}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
